import type { FC } from 'react'
import type { CardMeta } from '../dashboard/types'
import CardInfoButton from './CardInfoButton'

type DprSummaryProps = {
  dprList: any[]
  totalDprToday: number
  dprOverdue: number
  onViewAll: () => void
}

const meta: CardMeta = {
  description: 'Saved daily position reports, Drafts for today (UTC), and recorded activity-row counts.',
  endpoint: 'GET /dpr',
  logic: 'Today uses UTC. SUBMITTED and REVIEWED count as submitted. Average activities uses actual activities arrays; missing arrays are not estimated.',
}

const DprSummary: FC<DprSummaryProps> = ({ dprList, totalDprToday, dprOverdue, onViewAll }) => {
  const avgActivities = dprList.length > 0
    && dprList.every(d => Number.isFinite(d.activityCount))
    ? (dprList.reduce((sum, d) => sum + d.activityCount, 0) / dprList.length).toFixed(1)
    : 'Not available'

  return (
    <div className='flex flex-col flex-1'>
      <div className='card flex-1' style={{ borderRadius: '16px' }}>
        <div className='card-body'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>DPR Summary</h6>
            <span className='ms-auto d-flex align-items-center gap-2'>
              <CardInfoButton meta={meta} />
              <a href='#' className='fw-bold' style={{ fontSize: '10px' }} onClick={(e) => { e.preventDefault(); onViewAll(); }}>View all</a>
            </span>
          </div>
          <div className='row g-2 mb-3'>
            <div className='col-4 text-center'>
              <div className='fw-bold text-dark fs-5'>{totalDprToday}</div>
              <div className='text-muted' style={{ fontSize: '9px' }}>Submitted / Reviewed Today (UTC)</div>
            </div>
            <div className='col-4 text-center'>
              <div className='fw-bold text-danger fs-5'>{dprOverdue}</div>
              <div className='text-muted' style={{ fontSize: '9px' }}>Drafts Today (UTC)</div>
            </div>
            <div className='col-4 text-center'>
              <div className='fw-bold text-dark fs-5'>{avgActivities}</div>
              <div className='text-muted' style={{ fontSize: '9px' }}>Avg Activities</div>
            </div>
          </div>
          <div className='d-flex flex-column gap-2' style={{ fontSize: '10px' }}>
            {dprList.length > 0 ? (
              dprList.slice(0, 4).map((dpr: any, i: number) => (
                <DprRow key={i}
                  vessel={dpr.vesselName || dpr.vessel?.fleet_name || '—'}
                  date={dpr.reportDate ? new Date(dpr.reportDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—'}
                  status={dpr.status || 'Unknown'}
                  portSea={dpr.portOrSea || (typeof dpr.atSea === 'boolean' ? dpr.atSea ? 'SEA' : 'PORT' : 'Unknown')}
                  activities={Number.isFinite(dpr.activityCount) ? `${dpr.activityCount} activities` : '—'} />
              ))
            ) : (
              <div className='text-center text-muted py-3' style={{ fontSize: '11px' }}>
                <div className='mb-1'>No DPRs found</div>
                <div style={{ fontSize: '10px' }}>Daily reports will appear here</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const DprRow: FC<{ vessel: string; date: string; status: string; portSea: string; activities: string }> = ({ vessel, date, status, portSea, activities }) => (
  <div className='d-flex justify-content-between align-items-center p-2 rounded border' style={{ borderColor: '#eee', fontSize: '10px' }}>
    <div className='d-flex align-items-center gap-2'>
      <span className='fw-semibold text-dark'>{vessel}</span>
      <span className='text-muted'>{date}</span>
    </div>
    <div className='d-flex align-items-center gap-2'>
      <span className={`badge ${status === 'SUBMITTED' ? 'badge-light-success' : 'badge-light-warning'}`} style={{ fontSize: '8px' }}>{status}</span>
      <span className='text-muted'>{portSea}</span>
      <span className='text-muted'>{activities}</span>
    </div>
  </div>
)

export default DprSummary
